import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import Swal from 'sweetalert2';
import {UserService} from '../../../services/user/user.service';
import {AthleteService} from '../../../services/athlete/athlete.service';

interface Enrollment { id: string; season: string | null; category: string | null; status: string; createdAt: string; }
interface Payment { id: string; amount: number; status: string; paymentMethod?: string; receiptUrl?: string; createdAt: string; updatedAt: string; }
interface OverviewAthlete {
    id: string; firstName: string; lastName: string; fiscalCode?: string; bornDate?: string; status: string; createdAt: string;
    company?: { id: string; name: string }; enrollments: Enrollment[]; payments: Payment[];
}
interface ActivityEntry { type: 'account' | 'login' | 'athlete' | 'enrollment' | 'payment'; at: string; label: string; status?: string; }
interface Overview {
    user: any;
    athletes: OverviewAthlete[];
    messages: { received: number; unread: number; sent: number };
    activity: ActivityEntry[];
}

const STATUS_LABELS: Record<string, string> = {
    active: 'Attivo', deleted: 'Eliminato', pendingOtp: 'OTP da completare', pendingOnboardingOtp: 'OTP onboarding',
    pendingOnboarding: 'Onboarding in corso', pendingApprovation: 'In attesa di approvazione',
    pendingCompanyApprovation: 'In attesa della società', pendingPayment: 'Da pagare', partialPayment: 'Pagato in parte', paid: 'Pagato',
};

@Component({
    selector: 'app-admin-user-detail',
    templateUrl: './admin-user-detail.component.html',
})
export class AdminUserDetailComponent implements OnInit {
    overview?: Overview;
    loading = true;
    notFound = false;
    busy = false;
    copiedKey = '';

    readonly activityIcons: Record<ActivityEntry['type'], string> = {
        account: 'bi-person-plus', login: 'bi-box-arrow-in-right', athlete: 'bi-person-badge', enrollment: 'bi-clipboard-check', payment: 'bi-credit-card',
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private athleteService: AthleteService,
    ) {
    }

    async ngOnInit() {
        await this.load();
    }

    get user() {
        return this.overview?.user;
    }

    get activeAthletes(): OverviewAthlete[] {
        return this.overview?.athletes.filter((athlete) => athlete.status !== 'deleted') ?? [];
    }

    get deletedAthletes(): OverviewAthlete[] {
        return this.overview?.athletes.filter((athlete) => athlete.status === 'deleted') ?? [];
    }

    get paidTotal(): number {
        return this.sumPayments((payment) => payment.status === 'paid');
    }

    get pendingTotal(): number {
        return this.sumPayments((payment) => payment.status === 'pendingPayment' || payment.status === 'partialPayment');
    }

    async load() {
        const response = await this.userService.getUserOverview(this.route.snapshot.params['userId']);
        this.overview = response?.success ? response.data : undefined;
        this.notFound = !this.overview;
        this.loading = false;
    }

    statusLabel(status?: string): string {
        return (status && STATUS_LABELS[status]) || status || '—';
    }

    statusClasses(status?: string): string {
        if (status === 'active' || status === 'paid') return 'bg-emerald-50 text-emerald-700';
        if (status === 'deleted') return 'bg-red-50 text-red-700';
        if (status?.startsWith('pending') || status === 'partialPayment') return 'bg-amber-50 text-amber-700';
        return 'bg-slate-100 text-slate-600';
    }

    roleLabel(group?: string): string {
        return ({admin: 'Admin', agent: 'Agente', companyManager: 'Società', mister: 'Mister', user: 'Genitore / atleta'} as Record<string, string>)[group ?? ''] ?? group ?? '—';
    }

    initials(firstName?: string, lastName?: string, fallback?: string): string {
        return `${firstName?.trim().charAt(0) ?? ''}${lastName?.trim().charAt(0) ?? ''}`.toUpperCase() || fallback?.charAt(0).toUpperCase() || '?';
    }

    formatDate(value?: string, withTime = false): string {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return withTime
            ? date.toLocaleString('it-IT', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'})
            : date.toLocaleDateString('it-IT', {day: '2-digit', month: 'short', year: 'numeric'});
    }

    formatMoney(value: number): string {
        return value.toLocaleString('it-IT', {style: 'currency', currency: 'EUR'});
    }

    fullAddress(): string {
        const user = this.user;
        const parts = [user?.address, user?.addressNumber, user?.zip, user?.city, user?.province]
            .filter((part) => part !== undefined && part !== null && String(part).trim() !== '');
        return parts.length ? parts.join(', ') : '—';
    }

    copyValue(value: string | undefined, key: string) {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            this.copiedKey = key;
            setTimeout(() => this.copiedKey = '', 1400);
        });
    }

    async loginAs() {
        const response: any = await this.userService.loginAs(this.user.email, this.user.id);
        if (response?.success) setTimeout(() => window.location.reload(), 300);
        else Swal.fire({icon: 'error', title: 'Impossibile accedere come questo utente'});
    }

    async sendRecoveryLink() {
        const result = await Swal.fire({
            icon: 'question',
            title: 'Inviare il link di recupero?',
            html: `Riceverà un'email su <b>${this.escape(this.user.email)}</b> con il link e il codice per reimpostare la password.`,
            showCancelButton: true,
            confirmButtonText: 'Invia email',
            cancelButtonText: 'Annulla',
        });
        if (!result.isConfirmed) return;

        await this.run(async () => {
            const response = await this.userService.recoverPassword(this.user.email);
            if (response?.success) Swal.fire({icon: 'success', title: 'Email di recupero inviata'});
            else Swal.fire({icon: 'error', title: 'Invio non riuscito', text: 'Il recupero password funziona solo per utenti attivi.'});
        });
    }

    async changePassword() {
        const result = await Swal.fire({
            title: 'Imposta una nuova password',
            text: "La password attuale smetterà di funzionare. Comunicala all'utente in modo sicuro.",
            input: 'password',
            inputPlaceholder: 'Nuova password (min. 8 caratteri)',
            inputAttributes: {autocomplete: 'new-password', minlength: '8'},
            showCancelButton: true,
            confirmButtonText: 'Salva password',
            cancelButtonText: 'Annulla',
            inputValidator: (value) => (!value || value.length < 8) ? 'La password deve avere almeno 8 caratteri.' : null,
        });
        if (!result.isConfirmed || !result.value) return;

        await this.run(async () => {
            const response = await this.userService.adminSetPassword(this.user.id, result.value);
            if (response?.success) Swal.fire({icon: 'success', title: 'Password aggiornata'});
            else Swal.fire({icon: 'error', title: 'Aggiornamento non riuscito'});
        });
    }

    async deleteUser() {
        const athletes = this.activeAthletes;
        const result = await Swal.fire({
            icon: 'warning',
            title: `Eliminare ${this.escape(this.user.firstName)} ${this.escape(this.user.lastName)}?`,
            html: "L'utente non potrà più accedere. I dati restano nel database come eliminati.",
            input: athletes.length ? 'checkbox' : undefined,
            inputValue: 1,
            inputPlaceholder: athletes.length
                ? `Elimina anche ${athletes.length === 1 ? "l'atleta collegato" : `i ${athletes.length} atleti collegati`} (${athletes.map((a) => this.escape(`${a.firstName} ${a.lastName}`)).join(', ')})`
                : undefined,
            showCancelButton: true,
            confirmButtonText: 'Elimina utente',
            confirmButtonColor: '#dc2626',
            cancelButtonText: 'Annulla',
        });
        if (!result.isConfirmed) return;

        await this.run(async () => {
            const response: any = await this.userService.deleteUser(this.user.id);
            if (!response?.success) {
                Swal.fire({icon: 'error', title: "Errore nell'eliminazione dell'utente"});
                return;
            }
            if (athletes.length && result.value) {
                const results = await Promise.all(athletes.map((athlete) => this.athleteService.deleteAthlete(athlete.id)));
                if (results.some((r: any) => !r?.success)) Swal.fire({icon: 'warning', title: 'Utente eliminato, ma alcuni atleti non sono stati eliminati'});
                else Swal.fire({icon: 'success', title: 'Utente e atleti eliminati'});
            } else {
                Swal.fire({icon: 'success', title: 'Utente eliminato'});
            }
            await this.load();
        });
    }

    async deleteAthlete(athlete: OverviewAthlete) {
        const result = await Swal.fire({
            icon: 'warning',
            title: `Eliminare l'atleta ${this.escape(athlete.firstName)} ${this.escape(athlete.lastName)}?`,
            text: athlete.company ? `Non risulterà più tra gli atleti di ${athlete.company.name}.` : undefined,
            showCancelButton: true,
            confirmButtonText: 'Elimina atleta',
            confirmButtonColor: '#dc2626',
            cancelButtonText: 'Annulla',
        });
        if (!result.isConfirmed) return;

        await this.run(async () => {
            const response: any = await this.athleteService.deleteAthlete(athlete.id);
            if (response?.success) await this.load();
            else Swal.fire({icon: 'error', title: "Errore nell'eliminazione dell'atleta"});
        });
    }

    back() {
        this.router.navigate(['/admins/allUsers']);
    }

    private sumPayments(predicate: (payment: Payment) => boolean): number {
        return (this.overview?.athletes ?? [])
            .flatMap((athlete) => athlete.payments)
            .filter(predicate)
            .reduce((total, payment) => total + Number(payment.amount || 0), 0);
    }

    private async run(action: () => Promise<void>) {
        this.busy = true;
        try {
            await action();
        } finally {
            this.busy = false;
        }
    }

    // Names end up in SweetAlert html/title, which renders markup
    private escape(value?: string): string {
        return (value ?? '').replace(/[&<>"']/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]!));
    }
}
